// useUserlandWallets — state + actions for the Portfolio Wallets panel.
// Backed by the /v1/me wallet-management API (safe surface: metadata +
// browser-encrypted ciphertext only). When the backend gate is off or the API
// is unreachable the hook reports status 'offline' and the panel renders an
// honest empty state instead of sample rows.

import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../api/client'
import {
  type UserlandWallet,
  type WalletLimits,
  archiveWallet,
  clearPrimaryWallet,
  createWallet,
  deleteWallet,
  fetchWallets,
  getSessionAccountId,
  importWallet,
  onSessionChanged,
  renameWallet,
  setPrimaryWallet,
  setWalletSelected,
  unarchiveWallet,
} from '../api/userland'
import type { AccountSecretEnvelope } from '../lib/clientVault'
import type { TonWalletVersion } from '../lib/tonWalletVersions'
import { probeTradeCapabilities } from '../lib/tradeCapabilities'
// The TON crypto stack (tonMnemonic / tonWalletVersions → @ton/core, @ton/crypto,
// @ton/ton — ~700KB minified) is imported dynamically INSIDE the wallet actions
// below so it never lands in the boot chunk; wallet create/import pays the
// one-time chunk load on first use instead.

// EncryptSeedFn encrypts a seed phrase with the UNLOCKED account vault session
// (see useAccountVault). Wallet flows never see a password themselves.
export type EncryptSeedFn = (seedWords: string) => Promise<AccountSecretEnvelope>

export type WalletsStatus = 'loading' | 'ready' | 'offline'

export type UseUserlandWallets = {
  status: WalletsStatus
  /** Server exposes wallet mutation/vault routes, not only quote identity. */
  managementEnabled: boolean
  /** Server exposes encrypted-seed export/reveal. */
  exportEnabled: boolean
  wallets: UserlandWallet[]
  limits: WalletLimits | null
  actionError: string | null
  clearActionError: () => void
  reload: () => Promise<void>
  generateMnemonic: () => Promise<string[]>
  createFromMnemonic: (
    words: string[],
    label: string,
    encryptSeed: EncryptSeedFn
  ) => Promise<UserlandWallet>
  importFromInput: (
    input: string,
    label: string,
    encryptSeed: EncryptSeedFn,
    version?: TonWalletVersion
  ) => Promise<UserlandWallet>
  rename: (id: string, label: string) => Promise<void>
  remove: (id: string) => Promise<void>
  archive: (id: string) => Promise<void>
  unarchive: (id: string) => Promise<void>
  makePrimary: (id: string) => Promise<void>
  clearPrimary: (id: string) => Promise<void>
  toggleSelected: (wallet: UserlandWallet) => Promise<void>
}

export function useUserlandWallets(): UseUserlandWallets {
  const [status, setStatus] = useState<WalletsStatus>('loading')
  const [wallets, setWallets] = useState<UserlandWallet[]>([])
  const [limits, setLimits] = useState<WalletLimits | null>(null)
  const [managementEnabled, setManagementEnabled] = useState(false)
  const [exportEnabled, setExportEnabled] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const alive = useRef(true)
  const readGeneration = useRef(0)
  // the account the list was read for
  const listAccount = useRef(getSessionAccountId())

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      readGeneration.current += 1
    }
  }, [])

  const reload = useCallback(async () => {
    const generation = ++readGeneration.current
    try {
      const capabilities = await probeTradeCapabilities()
      if (!alive.current || generation !== readGeneration.current) return
      const resp = capabilities.walletManagementEnabled ? await fetchWallets() : null
      if (!alive.current || generation !== readGeneration.current) return
      setManagementEnabled(capabilities.walletManagementEnabled)
      setExportEnabled(capabilities.walletExportEnabled)
      setWallets(resp?.wallets ?? [])
      setLimits(resp?.limits ?? null)
      setStatus('ready')
    } catch (err) {
      if (!alive.current || generation !== readGeneration.current) return
      setManagementEnabled(false)
      setExportEnabled(false)
      setWallets([])
      setLimits(null)
      // 401 is a guest, not an offline backend. Only transport/5xx look offline.
      setStatus(err instanceof ApiError && err.status === 401 ? 'ready' : 'offline')
    }
  }, [])

  useEffect(() => {
    void reload()
    return onSessionChanged(() => {
      const account = getSessionAccountId()
      const previous = listAccount.current
      listAccount.current = account
      // The next account — or none — must never inherit this account's trading
      // identity while its own wallet request is pending or fails. A new
      // session of the SAME account (a re-login, the first /v1/me at boot)
      // keeps the list while it is read again: clearing it reset every trade
      // and transfer in flight for nothing (r9x review).
      if (previous !== null && previous !== account) {
        setWallets([])
        setLimits(null)
        setManagementEnabled(false)
        setExportEnabled(false)
        setActionError(null)
        setStatus('loading')
      }
      void reload()
    })
  }, [reload])

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setActionError(null)
      try {
        if (!managementEnabled) throw new Error('wallet management is disabled')
        await action()
        await reload()
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'wallet backend unreachable'
        if (alive.current) setActionError(message)
        throw err
      }
    },
    [managementEnabled, reload]
  )

  const createFromMnemonic = useCallback(
    async (words: string[], label: string, encryptSeed: EncryptSeedFn) => {
      setActionError(null)
      if (!managementEnabled) throw new Error('wallet management is disabled')
      const { deriveV5R1FromMnemonic, validateTonMnemonic } = await import('../lib/tonMnemonic')
      const ok = await validateTonMnemonic(words)
      if (!ok) throw new Error('generated mnemonic failed validation')
      const derived = await deriveV5R1FromMnemonic(words)
      const { persistWalletStateInit } = await import('../lib/walletStateInit')
      persistWalletStateInit(derived.address.friendly, derived.stateInitBocBase64)
      const { persistDeviceWalletSeed } = await import('../lib/deviceWalletSeed')
      await persistDeviceWalletSeed(derived.address.friendly, words.join(' '))
      const seed = await encryptSeed(words.join(' '))
      try {
        const wallet = await createWallet({
          label,
          address: derived.address.friendly,
          stateInitBocBase64: derived.stateInitBocBase64,
          seed,
        })
        await reload()
        return wallet
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'wallet backend unreachable'
        if (alive.current) setActionError(message)
        throw err
      }
    },
    [managementEnabled, reload]
  )

  const importFromInput = useCallback(
    async (
      input: string,
      label: string,
      encryptSeed: EncryptSeedFn,
      version: TonWalletVersion = 'v5r1'
    ) => {
      setActionError(null)
      if (!managementEnabled) throw new Error('wallet management is disabled')
      const { normalizeMnemonicInput, validateTonMnemonic } = await import('../lib/tonMnemonic')
      const { deriveWalletVersionCandidates } = await import('../lib/tonWalletVersions')
      const words = normalizeMnemonicInput(input)
      const ok = await validateTonMnemonic(words)
      if (!ok) throw new Error('invalid seed phrase (expected 24 valid TON words)')
      // Re-derive here (not trusting the modal's preview) so the stored
      // address always matches the chosen version for THIS seed.
      const candidates = await deriveWalletVersionCandidates(words)
      const chosen = candidates.find((c) => c.version === version)
      if (!chosen) throw new Error(`unsupported wallet version ${version}`)
      // v5r1 imports carry the same public constructor a created wallet does;
      // other versions have no v5r1 StateInit and are quoted only once deployed.
      let stateInitBocBase64: string | undefined
      if (version === 'v5r1') {
        const { deriveV5R1FromMnemonic } = await import('../lib/tonMnemonic')
        stateInitBocBase64 = (await deriveV5R1FromMnemonic(words)).stateInitBocBase64
      }
      const { persistDeviceWalletSeed } = await import('../lib/deviceWalletSeed')
      await persistDeviceWalletSeed(chosen.friendly, words.join(' '))
      const seed = await encryptSeed(words.join(' '))
      try {
        const wallet = await importWallet({
          label,
          address: chosen.friendly,
          walletVersion: version,
          stateInitBocBase64,
          seed,
        })
        await reload()
        return wallet
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'wallet backend unreachable'
        if (alive.current) setActionError(message)
        throw err
      }
    },
    [managementEnabled, reload]
  )

  return {
    status,
    managementEnabled,
    exportEnabled,
    wallets,
    limits,
    actionError,
    clearActionError: useCallback(() => setActionError(null), []),
    reload,
    generateMnemonic: useCallback(
      () => import('../lib/tonMnemonic').then((module) => module.generateTonMnemonic()),
      []
    ),
    createFromMnemonic,
    importFromInput,
    rename: useCallback(
      async (id, label) => {
        if (!managementEnabled) throw new Error('wallet management is disabled')
        const next = label.trim()
        setActionError(null)
        setWallets((list) => list.map((row) => (row.id === id ? { ...row, label: next } : row)))
        try {
          await renameWallet(id, next)
        } catch (err) {
          await reload()
          if (alive.current) {
            setActionError(err instanceof ApiError ? err.message : 'wallet backend unreachable')
          }
          throw err
        }
      },
      [managementEnabled, reload]
    ),
    remove: useCallback((id) => run(() => deleteWallet(id)), [run]),
    archive: useCallback((id) => run(() => archiveWallet(id)), [run]),
    unarchive: useCallback((id) => run(() => unarchiveWallet(id)), [run]),
    makePrimary: useCallback(
      async (id) => {
        if (!managementEnabled) throw new Error('wallet management is disabled')
        setActionError(null)
        setWallets((list) =>
          list.map((row) => ({
            ...row,
            is_primary: row.id === id,
            is_selected: row.id === id ? true : row.is_selected,
          }))
        )
        try {
          await setPrimaryWallet(id)
        } catch (err) {
          await reload()
          if (alive.current) {
            setActionError(err instanceof ApiError ? err.message : 'wallet backend unreachable')
          }
          throw err
        }
      },
      [managementEnabled, reload]
    ),
    clearPrimary: useCallback(
      async (id) => {
        if (!managementEnabled) throw new Error('wallet management is disabled')
        setActionError(null)
        setWallets((list) =>
          list.map((row) => (row.id === id ? { ...row, is_primary: false } : row))
        )
        try {
          await clearPrimaryWallet(id)
        } catch (err) {
          await reload()
          if (alive.current) {
            setActionError(err instanceof ApiError ? err.message : 'wallet backend unreachable')
          }
          throw err
        }
      },
      [managementEnabled, reload]
    ),
    toggleSelected: useCallback(async (wallet) => {
      const next = !wallet.is_selected
      setActionError(null)
      setWallets((list) =>
        list.map((row) => (row.id === wallet.id ? { ...row, is_selected: next } : row))
      )
      try {
        await setWalletSelected(wallet.id, next)
      } catch (err) {
        if (alive.current) {
          setWallets((list) =>
            list.map((row) =>
              row.id === wallet.id ? { ...row, is_selected: wallet.is_selected } : row
            )
          )
          setActionError(err instanceof ApiError ? err.message : 'wallet backend unreachable')
        }
        throw err
      }
    }, []),
  }
}
